import { useEffect, useState, type CSSProperties } from "react";
import { Icon } from "./lib/Icon";
import { Avatar } from "./lib/components";
import { NotificationBell } from "./lib/NotificationBell";
import { registerServiceWorker, requestPushPermissionAndSubscribe, getPushPermission, pushSupported } from "./api/push";
import {
  TweakColor,
  TweakRadio,
  TweakSection,
  TweakSelect,
  TweaksPanel,
  useTweaks,
} from "./lib/TweaksPanel";
import { ToastProvider } from "./lib/toast";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CatalogProvider } from "./contexts/CatalogContext";
import { InventoryProvider } from "./contexts/InventoryContext";
import { PhotosProvider } from "./contexts/PhotosContext";
import { AuthScreen } from "./screens/AuthScreen";
import { OnboardingScreen } from "./screens/OnboardingScreen";
import { Dashboard } from "./screens/Dashboard";
import { AlbumScreen } from "./screens/AlbumScreen";
import { MatchesScreen } from "./screens/MatchesScreen";
import { ChatScreen } from "./screens/ChatScreen";
import { MarketScreen } from "./screens/MarketScreen";
import { ProfileScreen } from "./screens/ProfileScreen";
import { AdminScreen } from "./screens/AdminScreen";
import { AlbumBookScreen } from "./screens/AlbumBookScreen";
import { CompleteProfileScreen } from "./screens/CompleteProfileScreen";
import { DonateScreen } from "./screens/DonateScreen";
import { TradesScreen } from "./screens/TradesScreen";
import { LegalScreen } from "./screens/TermsScreen";
import { MyStatsScreen } from "./screens/MyStatsScreen";
import { UsersScreen } from "./screens/UsersScreen";

export type Route =
  | "dashboard"
  | "album"
  | "album-book"
  | "owned"
  | "dups"
  | "missing"
  | "matches"
  | "chat"
  | "market"
  | "trades"
  | "stats"
  | "users"
  | "profile"
  | "admin"
  | "donate"
  | "terms"
  | "privacy"
  | "onboarding";

interface Tweaks {
  theme: "light" | "dark";
  primaryColor: string;
  accentColor: string;
  fontDisplay: string;
  fontBody: string;
  density: "comfortable" | "compact";
  stickerVariant: "default" | "flat" | "photo";
  matchVariant: "list" | "swipe" | "grid";
}

const DEFAULT_TWEAKS: Tweaks = {
  theme: "light",
  primaryColor: "#059669",
  accentColor: "#ec4899",
  fontDisplay: "Bricolage Grotesque",
  fontBody: "Inter Tight",
  density: "comfortable",
  stickerVariant: "default",
  matchVariant: "list",
};

const TITLES: Partial<Record<Route, string>> = {
  dashboard: "Inicio",
  album: "Mi álbum · vista lista",
  owned: "Mis pegadas",
  dups: "Mis repetidas",
  missing: "Mis faltantes",
  matches: "Matches automáticos",
  trades: "Tratos",
  chat: "Chat",
  market: "Mercado",
  profile: "Mi perfil",
  admin: "Centro de control · Nilabs",
  donate: "Apoyar el proyecto",
  terms: "Términos y condiciones",
  privacy: "Política de privacidad",
  "album-book": "Mi álbum",
  stats: "Mis estadísticas",
  users: "Coleccionistas",
};

export default function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <CatalogProvider>
          <InventoryProvider>
            <PhotosProvider>
              <Root />
            </PhotosProvider>
          </InventoryProvider>
        </CatalogProvider>
      </AuthProvider>
    </ToastProvider>
  );
}

function Root() {
  const { status, user } = useAuth();
  const [tweaks, setTweak] = useTweaks<Tweaks>(DEFAULT_TWEAKS);

  // /reset-password ya no existe — si alguien llega ahí (link antiguo en email),
  // limpiamos la URL y mostramos auth screen normal.
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.pathname === "/reset-password") {
      window.history.replaceState(null, "", "/");
    }
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = tweaks.theme;
    document.documentElement.dataset.density = tweaks.density;
    const root = document.documentElement.style;
    root.setProperty("--primary", tweaks.primaryColor);
    root.setProperty("--accent-magenta", tweaks.accentColor);
    root.setProperty("--font-display", `'${tweaks.fontDisplay}', system-ui, sans-serif`);
    root.setProperty("--font-body", `'${tweaks.fontBody}', system-ui, sans-serif`);
  }, [tweaks]);

  // Registrar Service Worker (idempotente). Solo el SW, sin pedir permiso.
  useEffect(() => {
    registerServiceWorker();
  }, []);

  if (status === "loading") {
    return (
      <div style={{ display: "grid", placeItems: "center", height: "100vh", color: "var(--fg-muted)" }}>
        <div style={{ textAlign: "center" }}>
          <div className="brand-mark" style={{ width: 56, height: 56, fontSize: 24, fontFamily: "var(--font-display)", margin: "0 auto 16px" }}>P!</div>
          <div>Cargando…</div>
        </div>
      </div>
    );
  }

  if (status === "guest" || !user) {
    return <AuthScreen />;
  }

  return <AppShell tweaks={tweaks} setTweak={setTweak} />;
}

interface AppShellProps {
  tweaks: Tweaks;
  setTweak: (key: keyof Tweaks | Partial<Tweaks>, value?: Tweaks[keyof Tweaks]) => void;
}

function AppShell({ tweaks, setTweak }: AppShellProps) {
  const { user, logout } = useAuth();
  const [route, setRoute] = useState<Route>("dashboard");
  const [chatTarget, setChatTarget] = useState<number | null>(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [completeProfileSkipped, setCompleteProfileSkipped] = useState(false);
  const [showPushBanner, setShowPushBanner] = useState(false);

  // Mostrar banner para activar push si:
  // - El navegador lo soporta
  // - El usuario no lo ha bloqueado/permitido aún
  // - No fue dismisseado anteriormente
  useEffect(() => {
    if (!user) return;
    if (!pushSupported()) return;
    const dismissedKey = `panini.push_banner_dismissed.${user.id}`;
    if (localStorage.getItem(dismissedKey)) return;
    getPushPermission().then((p) => {
      if (p === "default") setShowPushBanner(true);
    });
  }, [user]);

  const enablePush = async () => {
    try {
      const ok = await requestPushPermissionAndSubscribe();
      if (user) localStorage.setItem(`panini.push_banner_dismissed.${user.id}`, "1");
      setShowPushBanner(false);
      if (!ok) {
        // Permiso denegado o sin VAPID — silenciamos
      }
    } catch {
      setShowPushBanner(false);
    }
  };

  const dismissPushBanner = () => {
    if (user) localStorage.setItem(`panini.push_banner_dismissed.${user.id}`, "1");
    setShowPushBanner(false);
  };

  if (!user) return null;

  // Si el usuario NO tiene ciudad (típico tras signup por Google que solo trae
  // email + name), mostrar pantalla "Completa tu perfil" antes de entrar al app.
  // El usuario puede saltar pero le pedimos los datos para que aproveche la
  // proximidad geográfica en matches y mercado.
  if (!user.city && !completeProfileSkipped) {
    return <CompleteProfileScreen onDone={() => setCompleteProfileSkipped(true)} />;
  }

  const go = (r: Route) => {
    setRoute(r);
    setShowMobileMenu(false);
  };
  const openChat = (userId: number) => {
    setChatTarget(userId);
    setRoute("chat");
    setShowMobileMenu(false);
  };

  const isAdmin = user.role === "admin" || user.role === "super_admin";

  if (route === "onboarding") {
    return (
      <>
        <OnboardingScreen onFinish={() => setRoute("album")} />
        <TweaksPanelMounted tweaks={tweaks} setTweak={setTweak} />
      </>
    );
  }

  return (
    <>
      <div className="app-root">
        <aside className="side-rail">
          <div className="brand">
            <div className="brand-mark">P!</div>
            <div className="brand-name">
              Panini<span>Ni</span>labs
            </div>
          </div>

          <div className="nav-section">
            <NavItem icon="home" label="Inicio" active={route === "dashboard"} onClick={() => go("dashboard")} />
            <NavItem icon="book" label="Mi álbum" active={route === "album-book" || route === "album"} onClick={() => go("album-book")} />
            <NavItem icon="sparkle" label="Estadísticas" active={route === "stats"} onClick={() => go("stats")} />
            <NavItem icon="swap" label="Matches" active={route === "matches"} onClick={() => go("matches")} />
            <NavItem icon="user" label="Coleccionistas" active={route === "users"} onClick={() => go("users")} />
            <NavItem icon="hammer" label="Tratos" active={route === "trades"} onClick={() => go("trades")} />
            <NavItem icon="chat" label="Mensajes" active={route === "chat"} onClick={() => go("chat")} />
            <NavItem icon="store" label="Mercado" active={route === "market"} onClick={() => go("market")} />
          </div>

          <div className="nav-section">
            <div className="nav-section-title">Mi álbum</div>
            <NavItem icon="check" label="Pegadas" active={route === "owned"} onClick={() => go("owned")} />
            <NavItem icon="flame" label="Repetidas" active={route === "dups"} onClick={() => go("dups")} accent="amber" />
            <NavItem icon="heart" label="Faltantes" active={route === "missing"} onClick={() => go("missing")} accent="magenta" />
          </div>

          {isAdmin && (
            <div className="nav-section">
              <div className="nav-section-title">Nilabs</div>
              <NavItem icon="settings" label="Centro de control" active={route === "admin"} onClick={() => go("admin")} />
            </div>
          )}

          <div style={{ flex: 1 }} />

          <button
            className="btn btn-secondary"
            onClick={() => go("donate")}
            style={{ marginBottom: 8, justifyContent: "center", fontSize: 13 }}
            title="Apoyar el proyecto"
          >
            ☕ Apoyar el proyecto
          </button>

          <div style={{ background: "var(--bg-subtle)", borderRadius: 12, padding: 12, display: "flex", gap: 10, alignItems: "center", cursor: "pointer" }} onClick={() => go("profile")}>
            <Avatar user={user} />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>{user.name}</div>
              <div className="tiny muted">
                Nivel {user.level} · ⭐ {Number(user.stars_avg || 0).toFixed(1)}
              </div>
            </div>
            <button
              className="icon-btn"
              style={{ width: 32, height: 32 }}
              onClick={(e) => {
                e.stopPropagation();
                logout();
              }}
              title="Cerrar sesión"
            >
              <Icon name="logout" size={14} />
            </button>
          </div>
        </aside>

        <div className="app-main">
          <header className="topbar">
            <div className="page-title">{TITLES[route] || "PaniniNilabs"}</div>
            <div className="spacer" />
            <div className="search-box hide-mobile">
              <Icon name="search" size={16} />
              <input placeholder="Buscar láminas, jugadores, usuarios…" />
            </div>
            <NotificationBell go={go} openChat={openChat} />
            <button
              className="icon-btn"
              style={{ background: "transparent", border: 0, padding: 0 }}
              onClick={() => setShowMobileMenu((v) => !v)}
              aria-label="Menú"
            >
              <Avatar user={user} />
            </button>
          </header>
          {showMobileMenu && (
            <div className="user-menu-mobile" onClick={() => setShowMobileMenu(false)}>
              {/* Navegación principal — replica del sidebar para que en mobile
                  no queden secciones inalcanzables (Tratos, Estadísticas,
                  Pegadas, Repetidas, Faltantes no caben en el bottom-nav). */}
              <button className="nav-item" onClick={() => go("dashboard")}>
                <span className="icon"><Icon name="home" size={16} /></span> Inicio
              </button>
              <button className="nav-item" onClick={() => go("album-book")}>
                <span className="icon"><Icon name="book" size={16} /></span> Mi álbum
              </button>
              <button className="nav-item" onClick={() => go("stats")}>
                <span className="icon"><Icon name="sparkle" size={16} /></span> Estadísticas
              </button>
              <button className="nav-item" onClick={() => go("matches")}>
                <span className="icon"><Icon name="swap" size={16} /></span> Matches
              </button>
              <button className="nav-item" onClick={() => go("users")}>
                <span className="icon"><Icon name="user" size={16} /></span> Coleccionistas
              </button>
              <button className="nav-item" onClick={() => go("trades")}>
                <span className="icon"><Icon name="hammer" size={16} /></span> Tratos
              </button>
              <button className="nav-item" onClick={() => go("chat")}>
                <span className="icon"><Icon name="chat" size={16} /></span> Mensajes
              </button>
              <button className="nav-item" onClick={() => go("market")}>
                <span className="icon"><Icon name="store" size={16} /></span> Mercado
              </button>

              <div style={{ borderTop: "1px solid var(--border)", margin: "8px 0" }} />
              <div style={{ padding: "6px 12px", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "var(--fg-muted)" }}>Mi álbum</div>
              <button className="nav-item" onClick={() => go("owned")}>
                <span className="icon"><Icon name="check" size={16} /></span> Pegadas
              </button>
              <button className="nav-item" onClick={() => go("dups")}>
                <span className="icon"><Icon name="flame" size={16} /></span> Repetidas
              </button>
              <button className="nav-item" onClick={() => go("missing")}>
                <span className="icon"><Icon name="heart" size={16} /></span> Faltantes
              </button>

              <div style={{ borderTop: "1px solid var(--border)", margin: "8px 0" }} />
              <button className="nav-item" onClick={() => go("profile")}>
                <span className="icon"><Icon name="user" size={16} /></span> Mi perfil
              </button>
              <button className="nav-item" onClick={() => go("donate")}>
                <span className="icon">☕</span> Apoyar el proyecto
              </button>
              {isAdmin && (
                <button className="nav-item" onClick={() => go("admin")}>
                  <span className="icon"><Icon name="settings" size={16} /></span> Centro de control
                </button>
              )}
              <button className="nav-item" onClick={logout}>
                <span className="icon"><Icon name="logout" size={16} /></span> Cerrar sesión
              </button>
            </div>
          )}

          {route === "dashboard" && <Dashboard go={go} />}
          {route === "album" && <AlbumScreen filter="all" go={go} stickerVariant={tweaks.stickerVariant} />}
          {route === "album-book" && <AlbumBookScreen go={go} initialTeamId={null} />}
          {route === "owned" && <AlbumScreen filter="owned" go={go} stickerVariant={tweaks.stickerVariant} />}
          {route === "dups" && <AlbumScreen filter="duplicates" go={go} stickerVariant={tweaks.stickerVariant} />}
          {route === "missing" && <AlbumScreen filter="missing" go={go} stickerVariant={tweaks.stickerVariant} />}
          {route === "matches" && <MatchesScreen go={go} openChat={openChat} variant={tweaks.matchVariant} />}
          {route === "chat" && <ChatScreen initialUserId={chatTarget} />}
          {route === "market" && <MarketScreen openChat={openChat} />}
          {route === "trades" && <TradesScreen go={go} openChat={openChat} />}
          {route === "stats" && <MyStatsScreen go={go} />}
          {route === "users" && <UsersScreen go={go} openChat={openChat} />}
          {route === "profile" && <ProfileScreen go={go} />}
          {route === "admin" && (isAdmin ? <AdminScreen openChat={openChat} /> : <ForbiddenScreen />)}
          {route === "donate" && <DonateScreen />}
          {route === "terms" && <LegalScreen go={go} variant="terms" />}
          {route === "privacy" && <LegalScreen go={go} variant="privacy" />}

          <Footer go={go} />
        </div>
      </div>

      <BottomNav route={route} go={go} />

      {showPushBanner && (
        <div className="push-banner">
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, marginBottom: 2 }}>🔔 Activa notificaciones</div>
            <div className="tiny muted">Recibe alertas cuando alguien te proponga un trato o te escriba.</div>
          </div>
          <button className="btn btn-primary btn-sm" onClick={enablePush}>Activar</button>
          <button className="icon-btn" onClick={dismissPushBanner} aria-label="Cerrar">
            <Icon name="x" size={14} />
          </button>
        </div>
      )}

      <TweaksPanelMounted tweaks={tweaks} setTweak={setTweak} />
    </>
  );
}

interface NavItemProps {
  icon: string;
  label: string;
  active: boolean;
  onClick: () => void;
  badge?: number;
  accent?: "amber" | "magenta";
}

function NavItem({ icon, label, active, onClick, badge, accent }: NavItemProps) {
  return (
    <button className={"nav-item " + (active ? "active" : "")} onClick={onClick}>
      <span className="icon">
        <Icon name={icon} size={18} />
      </span>
      <span>{label}</span>
      {badge ? (
        <span
          className="badge-count"
          style={accent === "amber" ? ({ background: "var(--accent-amber)", color: "#4a2900" } as CSSProperties) : undefined}
        >
          {badge}
        </span>
      ) : null}
    </button>
  );
}

interface BottomNavProps {
  route: Route;
  go: (r: Route) => void;
}

function BottomNav({ route, go }: BottomNavProps) {
  const items: { r: Route; icon: string; label: string }[] = [
    { r: "dashboard", icon: "home", label: "Inicio" },
    { r: "album-book", icon: "book", label: "Álbum" },
    { r: "matches", icon: "swap", label: "Matches" },
    { r: "market", icon: "store", label: "Mercado" },
    { r: "chat", icon: "chat", label: "Chat" },
  ];
  return (
    <nav className="bottom-nav" aria-label="Navegación principal">
      {items.map((it) => {
        const active = route === it.r || (it.r === "album-book" && (route === "album" || route === "owned" || route === "dups" || route === "missing"));
        return (
          <button
            key={it.r}
            className={"bottom-nav-item " + (active ? "active" : "")}
            onClick={() => go(it.r)}
          >
            <span className="icon-wrap"><Icon name={it.icon} size={20} /></span>
            <span>{it.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

function Footer({ go }: { go: (r: Route) => void }) {
  return (
    <footer className="app-footer">
      <div className="app-footer-left">
        <span style={{ fontWeight: 700, fontFamily: "var(--font-display)" }}>PaniniNilabs</span>
        <span className="tiny muted"> · una iniciativa de </span>
        <a href="https://nilabs.co" target="_blank" rel="noopener noreferrer" className="footer-link">Nilabs</a>
      </div>
      <div className="app-footer-right">
        <a onClick={() => go("terms")} className="footer-link">Términos</a>
        <a onClick={() => go("privacy")} className="footer-link">Privacidad</a>
        <a onClick={() => go("donate")} className="footer-link">☕ Apoyar</a>
        <a href="mailto:contacto@nilabs.co" className="footer-link">contacto@nilabs.co</a>
      </div>
    </footer>
  );
}

function ForbiddenScreen() {
  return (
    <div className="page" style={{ textAlign: "center", paddingTop: 80 }}>
      <h1 style={{ fontFamily: "var(--font-display)", fontSize: 28 }}>Acceso restringido</h1>
      <p style={{ color: "var(--fg-muted)" }}>Solo para administradores Nilabs.</p>
    </div>
  );
}

interface TweakProps {
  tweaks: Tweaks;
  setTweak: (key: keyof Tweaks | Partial<Tweaks>, value?: Tweaks[keyof Tweaks]) => void;
}

function TweaksPanelMounted({ tweaks, setTweak }: TweakProps) {
  return (
    <TweaksPanel title="Tweaks">
      <TweakSection label="Apariencia" />
      <TweakRadio
        label="Tema"
        value={tweaks.theme}
        onChange={(v) => setTweak("theme", v as Tweaks["theme"])}
        options={[
          { value: "light", label: "Claro" },
          { value: "dark", label: "Oscuro" },
        ]}
      />
      <TweakRadio
        label="Densidad"
        value={tweaks.density}
        onChange={(v) => setTweak("density", v as Tweaks["density"])}
        options={[
          { value: "comfortable", label: "Cómoda" },
          { value: "compact", label: "Compacta" },
        ]}
      />

      <TweakSection label="Paleta" />
      <TweakColor label="Primario" value={tweaks.primaryColor} onChange={(v) => setTweak("primaryColor", v)} />
      <TweakColor label="Acento" value={tweaks.accentColor} onChange={(v) => setTweak("accentColor", v)} />
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
        {[
          { p: "#059669", a: "#ec4899", name: "Cancha" },
          { p: "#2563eb", a: "#f59e0b", name: "Cobalto" },
          { p: "#dc2626", a: "#1d4ed8", name: "Clásico" },
          { p: "#7c3aed", a: "#10b981", name: "Violeta" },
          { p: "#0891b2", a: "#f97316", name: "Cyan" },
        ].map((p) => (
          <button
            key={p.name}
            onClick={() => setTweak({ primaryColor: p.p, accentColor: p.a })}
            style={{
              padding: "6px 10px",
              borderRadius: 8,
              border: "1px solid var(--border)",
              background: "var(--bg-elev)",
              fontSize: 12,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 6,
              cursor: "pointer",
            }}
          >
            <span style={{ width: 12, height: 12, borderRadius: 3, background: p.p, display: "inline-block" }} />
            <span style={{ width: 12, height: 12, borderRadius: 3, background: p.a, display: "inline-block" }} />
            {p.name}
          </button>
        ))}
      </div>

      <TweakSection label="Tipografía" />
      <TweakSelect
        label="Display"
        value={tweaks.fontDisplay}
        onChange={(v) => setTweak("fontDisplay", v)}
        options={[
          { value: "Bricolage Grotesque", label: "Bricolage Grotesque" },
          { value: "Archivo", label: "Archivo" },
          { value: "Space Grotesk", label: "Space Grotesk" },
          { value: "DM Serif Display", label: "DM Serif Display" },
          { value: "Anton", label: "Anton (deportivo)" },
        ]}
      />
      <TweakSelect
        label="Cuerpo"
        value={tweaks.fontBody}
        onChange={(v) => setTweak("fontBody", v)}
        options={[
          { value: "Inter Tight", label: "Inter Tight" },
          { value: "DM Sans", label: "DM Sans" },
          { value: "Manrope", label: "Manrope" },
          { value: "IBM Plex Sans", label: "IBM Plex Sans" },
        ]}
      />

      <TweakSection label="Variantes" />
      <TweakRadio
        label="Lámina"
        value={tweaks.stickerVariant}
        onChange={(v) => setTweak("stickerVariant", v as Tweaks["stickerVariant"])}
        options={[
          { value: "default", label: "Default" },
          { value: "flat", label: "Flat" },
          { value: "photo", label: "Foto" },
        ]}
      />
      <TweakRadio
        label="Vista de matches"
        value={tweaks.matchVariant}
        onChange={(v) => setTweak("matchVariant", v as Tweaks["matchVariant"])}
        options={[
          { value: "list", label: "Lista" },
          { value: "swipe", label: "Swipe" },
          { value: "grid", label: "Grid" },
        ]}
      />
    </TweaksPanel>
  );
}
